import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../components/ui/FoxSpinner';
import { applicationsAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Crown, Trophy } from 'lucide-react';

const TopAfiliados = () => {
  const [winners, setWinners] = useState({ week_start: null, agentes: [], hosts: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWinners();
  }, []);

  const loadWinners = async () => {
    try {
      const response = await applicationsAPI.getCurrentWinners();
      setWinners(response.data);
    } catch (error) {
      console.error('Erro ao carregar ganhadores:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-orange-100 to-pink-100 flex items-center justify-center">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  const hasWinners = winners.agentes.length > 0 || winners.hosts.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-100 via-orange-100 to-pink-100 p-4">
      <div className="max-w-6xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4">
            <span className="text-orange-500">🦊 FOX</span>{' '}
            <span className="text-purple-600">SELLER</span>
          </h1>
          <div className="flex items-center justify-center gap-3 mb-4">
            <Crown className="h-12 w-12 text-yellow-600" />
            <h2 className="text-4xl font-bold text-yellow-700">
              TOP 3 DA SEMANA
            </h2>
            <Crown className="h-12 w-12 text-yellow-600" />
          </div>
          {winners.week_start && (
            <p className="text-lg text-gray-700">
              Semana de {new Date(winners.week_start).toLocaleDateString('pt-BR')}
            </p>
          )}
        </div>

        {!hasWinners ? (
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="p-12 text-center">
              <Trophy className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <p className="text-xl text-gray-600">
                Nenhum vencedor esta semana ainda
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Os ganhadores serão revelados aos Domingos
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {/* TOP 3 AGENTES */}
            <Card className="bg-gradient-to-br from-purple-500 to-blue-500 text-white shadow-2xl">
              <CardHeader>
                <CardTitle className="text-center text-2xl">
                  💼 TOP 3 AGENTES AFILIADOS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {winners.agentes.length === 0 ? (
                  <p className="text-center py-8 opacity-80">Nenhum agente esta semana</p>
                ) : (
                  winners.agentes.map((winner) => (
                    <Card 
                      key={winner.id}
                      className={`${
                        winner.position === 1 ? 'bg-gradient-to-r from-yellow-300 to-yellow-400 border-4 border-yellow-500' :
                        winner.position === 2 ? 'bg-gradient-to-r from-gray-200 to-gray-300 border-4 border-gray-400' :
                        'bg-gradient-to-r from-orange-200 to-orange-300 border-4 border-orange-400'
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {/* Medalha */}
                          <div className="text-6xl">
                            {winner.position === 1 && '🥇'}
                            {winner.position === 2 && '🥈'}
                            {winner.position === 3 && '🥉'}
                          </div>
                          
                          {/* Avatar */}
                          {winner.kako_avatar && (
                            <img 
                              src={winner.kako_avatar} 
                              alt={winner.nickname}
                              className="w-20 h-20 rounded-full border-4 border-white shadow-lg"
                            />
                          )}
                          
                          {/* Info */}
                          <div className="flex-1">
                            <div className="text-2xl font-bold text-gray-900">{winner.nickname}</div>
                            <div className="text-lg text-gray-700 mt-1">
                              {winner.week_coins.toLocaleString()} 🪙
                            </div>
                            <div className="text-sm text-gray-600">
                              {winner.week_orders} pedido(s)
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>

            {/* TOP 3 HOSTS */}
            <Card className="bg-gradient-to-br from-pink-500 to-red-500 text-white shadow-2xl">
              <CardHeader>
                <CardTitle className="text-center text-2xl">
                  🎭 TOP 3 HOSTS AFILIADOS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {winners.hosts.length === 0 ? (
                  <p className="text-center py-8 opacity-80">Nenhum host esta semana</p>
                ) : (
                  winners.hosts.map((winner) => (
                    <Card 
                      key={winner.id}
                      className={`${
                        winner.position === 1 ? 'bg-gradient-to-r from-yellow-300 to-yellow-400 border-4 border-yellow-500' :
                        winner.position === 2 ? 'bg-gradient-to-r from-gray-200 to-gray-300 border-4 border-gray-400' :
                        'bg-gradient-to-r from-orange-200 to-orange-300 border-4 border-orange-400'
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {/* Medalha */}
                          <div className="text-6xl">
                            {winner.position === 1 && '🥇'}
                            {winner.position === 2 && '🥈'}
                            {winner.position === 3 && '🥉'}
                          </div>
                          
                          {/* Avatar */}
                          {winner.kako_avatar && (
                            <img 
                              src={winner.kako_avatar} 
                              alt={winner.nickname}
                              className="w-20 h-20 rounded-full border-4 border-white shadow-lg"
                            />
                          )}
                          
                          {/* Info */}
                          <div className="flex-1">
                            <div className="text-2xl font-bold text-gray-900">{winner.nickname}</div>
                            <div className="text-lg text-gray-700 mt-1">
                              {winner.week_coins.toLocaleString()} 🪙
                            </div>
                            <div className="text-sm text-gray-600">
                              {winner.week_orders} pedido(s)
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12">
          <p className="text-gray-600">
            🦊 FOX SELLER - Sistema de Vendas KAKO LIVE
          </p>
        </div>
      </div>
    </div>
  );
};

export default TopAfiliados;
